// src/client/renderer/vfx/tile/warp-swirl-pool.ts
//
// Plan 16, Task 4.1 — Warp swirl pool.
//
// Triggered when a player crosses a `teleport`-type connection during movement.
// The resolver inspects MovementStepEvent and uses an injected getConnectionType
// callback (supplied by Plan 19) to determine whether the connection is a teleport.
// Returns null if the callback is absent or returns a non-teleport value.
//
// Geometry choice: THREE.CircleGeometry(0.6, 32), lying flat on the XZ plane
// (mesh.rotation.x = -PI/2). A disc (not a ring) gives a "whirlpool" silhouette
// that suggests a portal opening from nothing. The additive material colour blends
// with the tile surface rather than occluding it.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.600s  rotation.z: 0 → 2π  (full CCW swirl, proxy drives mesh.rotation.z)
//   0.000–0.600s  scale: 0 → 1.2       (proxy drives mesh.scale uniformly)
//   0.000–0.200s  opacity: 0 → 1       (fade-in — proxy drives material.opacity)
//   0.200–0.600s  opacity: 1 → 0       (fade-out — sequential after fade-in)
// Opacity curve intent: "appearing whirlpool" — snaps into existence then dissolves,
// peak visibility at 200ms, fully transparent at 600ms.
// Total lifetime: 600ms. One-shot — isFinished set via tl.call(fn, undefined, 0.6)
// (NOT eventCallback — see gsap-registry.ts VIRTUAL_DURATION note).
//
// Colour: 0xc063ff (purple, matches soul-anchor palette and AMBIENT_GLOW_COLORS.soul_anchor).
//
// Max-active: 4 (teleport connections are rare; 4 simultaneous crossings is generous).
//
// Position note: MovementStepEvent carries fromNodeId and toNodeId but no world-position.
// The resolver supplies a {x:0,y:0,z:0} placeholder — the wire-up site (Task 5.1)
// resolves nodeId→world-pos and passes the real position via manager.spawn().
// This follows the same pattern as resolveEventTileTriggerForEvent (Task 3.3).
//
// Forward-defense F4: pool.dispose() iterates active emitters, kills timelines,
//   frees per-emitter materials, then disposes shared geometry once. Idempotent.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const LIFETIME = 0.6; // seconds (600ms)

const SCALE_END = 1.2;
const ROTATION_END = Math.PI * 2; // full 360°

const FADE_IN_DURATION = 0.2; // 0 → 200ms: opacity 0→1
const FADE_OUT_DURATION = 0.4; // 200 → 600ms: opacity 1→0
const FADE_IN_START = 0.0;
const FADE_OUT_START = FADE_IN_DURATION; // 0.2s

/** Warp portal purple — matches soul-anchor palette (Task 3.2, Task 2.1). */
const SWIRL_COLOR = 0xc063ff;

/** Max concurrent active emitters. */
const MAX_ACTIVE = 4;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (CircleGeometry — flat disc, lying on XZ plane)
// ─────────────────────────────────────────────────────────────────────────────

const CIRCLE_RADIUS = 0.6;
const CIRCLE_SEGMENTS = 32;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface WarpSwirlPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createWarpSwirlPool({ scene, registry }: WarpSwirlPoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.CircleGeometry(CIRCLE_RADIUS, CIRCLE_SEGMENTS);

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Per-emitter material clone (additive blend — purple whirlpool glow on any background)
    const material = new THREE.MeshBasicMaterial({
      color: SWIRL_COLOR,
      transparent: true,
      opacity: 0.0, // starts invisible (fade-in begins immediately)
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);

    // Lie flat on the XZ tile surface — disc becomes a horizontal whirlpool
    mesh.rotation.x = -Math.PI / 2;
    mesh.rotation.z = 0;

    // Start at zero scale
    mesh.scale.set(0, 0, 0);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('warp_swirl');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween 1: swirl rotation 0 → 2π over 600ms (proxy drives mesh.rotation.z)
    const rotProxy = { value: 0 };
    tl.to(
      rotProxy,
      {
        value: ROTATION_END,
        duration: LIFETIME,
        ease: 'power1.inOut',
        onUpdate: () => {
          mesh.rotation.z = rotProxy.value;
        },
      },
      0,
    );

    // Tween 2: scale 0 → 1.2 over 600ms (proxy drives mesh.scale uniformly)
    const scaleProxy = { value: 0 };
    tl.to(
      scaleProxy,
      {
        value: SCALE_END,
        duration: LIFETIME,
        ease: 'power2.out',
        onUpdate: () => {
          mesh.scale.set(scaleProxy.value, scaleProxy.value, scaleProxy.value);
        },
      },
      0,
    );

    // Tween 3a: opacity fade-in 0 → 1 over first 200ms
    const opacityProxy = { value: 0.0 };
    tl.to(
      opacityProxy,
      {
        value: 1.0,
        duration: FADE_IN_DURATION,
        ease: 'power1.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      FADE_IN_START,
    );

    // Tween 3b: opacity fade-out 1 → 0 over remaining 400ms (sequential after fade-in)
    tl.to(
      opacityProxy,
      {
        value: 0.0,
        duration: FADE_OUT_DURATION,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      FADE_OUT_START,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method returns the
  // current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees ALL GPU resources
  // WITHOUT expecting prior release() calls. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `MovementStepEvent` with a teleport connection to warp swirl spawn args.
 *
 * Returns null for:
 *   - any non-movement_step event
 *   - movement_step when getConnectionType is not provided
 *   - movement_step when getConnectionType returns 'walk', 'jump', or undefined
 *
 * Position note: MovementStepEvent (src/shared/game/types.ts:340-347) carries
 * fromNodeId and toNodeId but NO world-position. The position placeholder
 * {x:0, y:0, z:0} is intentional — the wire-up site (Task 5.1) resolves
 * nodeId→world-pos and passes the real position via manager.spawn(). This
 * follows the same pattern as resolveEventTileTriggerForEvent (Task 3.3).
 *
 * The getConnectionType callback is supplied by Plan 19. For Plan 16,
 * this resolver returns null whenever the callback is absent, letting the
 * pool participate in the standard event bus without hard-coupling to the
 * board graph.
 */
export function resolveWarpSwirlForEvent(
  event: GameEvent,
  getConnectionType?: (
    fromNodeId: string,
    toNodeId: string,
  ) => 'walk' | 'jump' | 'teleport' | undefined,
): { poolId: 'warp_swirl'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'movement_step') return null;
  if (!getConnectionType) return null;

  const connectionType = getConnectionType(event.fromNodeId, event.toNodeId);
  if (connectionType !== 'teleport') return null;

  return {
    poolId: 'warp_swirl',
    args: {
      position: { x: 0, y: 0, z: 0 }, // wire-up (Task 5.1) supplies real destination position
    },
  };
}
